import {
  Component,
  OnInit,
  ViewChild,
  QueryList,
  ViewChildren,
  AfterViewInit,
  ElementRef,
  TemplateRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { take } from 'rxjs/operators';
import { ApplicationState } from '../../classes/ApplicationState';
import { ApplicationService } from '../../services/application/application.service';
import { AmorphicService } from '../../services/amorphic/amorphic.service';
import { ToasterComponent } from '../overlays/toaster/toaster.component';
import { ToastService } from '../overlays/toaster/toast-service.service';
import { CasMetadataNode } from '../../classes/CasMetadataNode';
import { CoverpathUtils } from '../../../js/static/CoverpathUtils';
import { AMCAuth0Service } from '../../services/authentication/amc/amc-auth0.service';
import { ModalService } from '../../services/modal/modal.service';
import { CPMetadataFilingTypes, ModalType } from '../../../js/interfaces/CoverpathTypes';
import { NavigationService } from '../../services/navigation/navigation.service';
import { CoverpathControllerInterface } from '../../interfaces/controller/CoverpathControllerInterface';
import { Utils } from '../../../../common/js/Utils';
import { ApplicationSourceType } from '../../../../common/js/constants/application';
import type { CoverpathPolicy } from '../../../classes/coverpath_subclasses/CoverpathPolicy';
import { SubjectManager } from '../../../../common/js/subjectManager/SubjectManager';
import { CoverpathCustomer } from '../../../classes/coverpath_subclasses/CoverpathCustomer';
import type { CasApplication } from '../../../classes/coverpath_subclasses/CasApplication';
import type { CoverpathBasePolicy } from '../../../classes/coverpath_subclasses/CoverpathBasePolicy';
import { MMDirectCustomer } from '../../../classes/mmdirect_subclasses/MMDirectCustomer';
import { MMDirectApplication } from '../../../classes/mmdirect_subclasses/MMDirectApplication';
import { BaseFormComponent } from '../form/base-form.component';
import { PartnerApplicationFillService } from '../../../../common/angular-shared/services/application/partner-application-fill.service';
import { AutofillService } from '../../services/application/autofill.service';
import { CpDialog } from '../../../components/dialog/dialog.service';
import { CpDialogRef } from '../../../components/dialog/dialog.ref';
import {
  CPGenericModalComponent,
  CPGenericModalResult,
} from '../../../components/cp-generic-modal/cp-generic-modal.component';
import { AlertIntent } from '../shared/alert/alert.component';
import { AgentController, TELE_CASE_STATUS } from '../../../../coverpath-agent/public/js/types/Types';
import { Subscription } from 'rxjs';
import { ApplicationSectionsList, ApplicationSectionUtils } from '../../classes/ApplicationSection';
import { GiactUtils } from '../../../../common/js/GiactUtils';
import { ApplicationDataService } from '../../../js/static/ApplicationDataService';

@Component({
  moduleId: module.id,
  selector: 'application-form',
  templateUrl: './application-form.component.html',
})
export class ApplicationFormComponent implements OnInit, AfterViewInit {
  appState: ApplicationState;
  isMetadataLoaded: boolean = false;
  showAdvisorAnsweredBanner: boolean;
  showAutoFill: boolean = false;
  showNewFilingNotice: boolean = false;
  showAMCemailVerify: boolean = false;
  currentSection: number;
  currentSectionName: string;
  isCurrentSectionCIR: boolean = false;
  showReadOnlyBanner: boolean = false;
  isHeavyUserEnabled: boolean = false;
  is2020CoverpathOrMMD: boolean = false;
  userFirstLastName: string;
  isLifeBridge: boolean = false;
  showAdvisorEditBanner = false;
  isLoading: boolean = false;
  isNewYorkApplication: boolean;
  isCAApplication: boolean;
  isTeleCMI: boolean;
  isTeleInterviewerLoggedIn: boolean;
  isTeleCMICompleted: boolean = false;
  teleCMIStatus: string;
  alertIntent = AlertIntent;
  teleCaseStatus = TELE_CASE_STATUS;
  loadingSubscription: Subscription;
  activeApplicationSubscription: Subscription;
  applicationSectionChangesSubscription: Subscription;
  editTeleCMIModalSubscription: Subscription;
  afterActiveSectionLoadedSubscription: Subscription;
  toastBySubjectOpen: boolean = false;
  missedQuestionSubscription: Subscription;
  teleMissedQuestionSubscription: Subscription;
  toastIsOpenSubscription: Subscription;

  @ViewChild('applicationToaster') applicationToaster: ToasterComponent;
  @ViewChild('topBanner') topBanner: ElementRef;
  @ViewChild('topBannerContent') topBannerContent: ElementRef;
  @ViewChildren('applicationSections') applicationSections: QueryList<BaseFormComponent>;
  @ViewChild('editTeleCmiModalTpl') editTeleCmiModalTpl: TemplateRef<void>;

  editTeleCmiModal: CpDialogRef<CPGenericModalComponent, CPGenericModalResult>;

  protected controller: CoverpathControllerInterface;
  constructor(
    private applicationService: ApplicationService,
    private amorphicService: AmorphicService,
    private router: Router,
    private authService: AMCAuth0Service,
    private modalService: ModalService,
    private dialog: CpDialog,
    private navigationService: NavigationService,
    private partnerFillService: PartnerApplicationFillService,
    private autofillService: AutofillService,
    private toastService: ToastService
  ) {
    this.appState = this.applicationService.appState;
    this.controller = amorphicService.controller;
    const customer: CoverpathCustomer | MMDirectCustomer = this.controller.customer;
    const application: CasApplication | MMDirectApplication = customer && customer.application;
    const primaryPolicy: CoverpathBasePolicy = application && (application.primaryPolicy as CoverpathBasePolicy);
    this.isNewYorkApplication = primaryPolicy && primaryPolicy.isNewYorkApplication();
    this.isCAApplication = primaryPolicy && primaryPolicy.isCAApplication();
    this.partnerFillService.setApplicationPropertiesMap();
    this.isTeleCMI = application.isTeleCMI;
    this.isTeleCMICompleted = application.isTeleCMICompleted;
  }

  async ngOnInit() {
    this.showAutoFill = this.amorphicService.controller.nonProdMode;
    this.isTeleInterviewerLoggedIn = this.appState.isTeleInterviewerLoggedIn;
    if (this.isTeleCMI) {
      this.teleCMIStatus = await this.controller.accountCenterController.getTeleCMIStatus(
        this.controller.customer.application as CasApplication
      );
    }
    this.showAMCemailVerify =
      this.amorphicService.controller.nonProdMode &&
      this.authService.getController().isAuth0Enabled &&
      !this.appState.isAdvisor;
    this.isHeavyUserEnabled = this.authService.getController().isHeavyUserEnabled;
    this.userFirstLastName = this.authService.getController().customerFirstLastName;

    this.loadingSubscription = this.applicationService.loadingSubject.subscribe(
      (isLoading) => (this.isLoading = isLoading)
    );

    await this.applicationService.resolveApplicationData();
    this.isMetadataLoaded = true;
    this.appState.initPercentComplete();
    this.showReadOnlyBanner = !!this.amorphicService.controller.isLocked;
    const customer = this.amorphicService.controller.customer;
    const application = customer.application;

    // if application is Advisor edit status and it is a draft that means it was edited by the new edit app feature.
    if (
      application.sourceType === ApplicationSourceType.ADVISOR_EDIT &&
      application.isDraftApplication((customer as CoverpathCustomer).hasRegistered)
    ) {
      this.showAdvisorEditBanner = true;
    }

    this.is2020CoverpathOrMMD = application.primaryPolicy.is2020CoverpathOrMMD();

    if (!this.appState.isAdvisor) {
      this.showAdvisorAnsweredBanner = application.isAgentAssistant;
      this.appState.buildEnabledQuestions();

      //Customer filling application before July 24th will be prompted to fill 2018 application again
      const filingDate = new Date('2018-07-24');
      this.showNewFilingNotice =
        application.createdTime < filingDate && (customer.progress === 11 || customer.progress === 12);
    }

    await this.resolveAMCuserData();
    this.isLifeBridge = this.amorphicService.controller.customer.application.primaryPolicy.isLifeBridge();
    this.activeApplicationSubscription = this.navigationService.activeApplicationSection$.subscribe((activeSection) => {
      this.currentSection = activeSection;
      const cpFilingType = this.appState.controller.customer.getCoverpathFilingType();
      this.currentSectionName = ApplicationSectionUtils.getAllSections(cpFilingType, this.appState)[activeSection];
      this.isCurrentSectionCIR = this.currentSectionName === ApplicationSectionsList.CHRONIC_ILLNESS_RIDER;
    });

    this.missedQuestionSubscription = this.applicationService.missedQuestionSubject.subscribe(() => {
      this.showSectionValidationToast();
    });

    this.teleMissedQuestionSubscription = this.applicationService.teleMissedQuestionSubject.subscribe(() => {
      this.navigateToErrorSection();
    });

    this.toastIsOpenSubscription = this.toastService.isOpen.subscribe((isOpen: boolean) => {
      this.toastBySubjectOpen = isOpen;
    });
  }

  ngAfterViewInit() {
    if (this.controller.partnerPrefillDataObject) {
      this.applicationSectionChangesSubscription = this.applicationSections.changes.subscribe(() => {
        // after ngFor loading all relevant base form components, clear the application data
        this.partnerFillService.partnerPropertiesMap = null;
        this.controller.partnerPrefillDataObject.clearPartnerApplicationData();
      });
    }
    this.listenSticky();
  }

  getFilingNumber() {
    const primaryPolicy = this.amorphicService.controller.customer.application.primaryPolicy;
    return CoverpathUtils.getFilingNumber(primaryPolicy);
  }

  async showEditTeleCmiModal(): Promise<void> {
    this.editTeleCmiModal = this.dialog.open(CPGenericModalComponent, {
      usePOC: true,
      data: {
        title: 'Edit CMI',
        subTitle: 'Are you sure?',
        contentTemplate: this.editTeleCmiModalTpl,
        showCTA: true,
        confirmText: 'Continue',
        cancelText: 'Cancel',
      },
    });
    this.editTeleCMIModalSubscription = this.editTeleCmiModal
      .afterClosed()
      .pipe(take(1))
      .subscribe(async ({ trigger }) => {
        if (trigger === 'CONFIRM') {
          const application = this.controller.customer.application;
          await (this.controller as AgentController).applicationSubmissionController.teleCMINeededAgain(
            application as CasApplication
          );
          this.applicationService.sendButtonDisabledSubject.next(false);
          this.applicationService.isDisabledSubject.next();
          this.isTeleCMICompleted = application.isTeleCMICompleted;
        } else {
          this.editTeleCmiModal.close();
        }
      });
  }

  // AMCAuthenticationResolver fetches AMCuserInfo
  async resolveAMCuserData(): Promise<void> {
    if (this.appState && this.appState.isAdvisor) {
      await this.authService.handlePhoneRegistrationForMfa();
    }
    await this.authService.createIdentityForCustomer();
  }

  async _testAutofillCoverpath() {
    const policy = this.amorphicService.controller.customer.application.primaryPolicy;
    this.autofillService.autoFillCoverpath(policy as CoverpathPolicy);

    await ApplicationDataService.updateWOPAndPremium(this.amorphicService.controller, policy);
    await this.amorphicService.controller.publicSave();
    await this.amorphicService.controller.handlePiiAfterAutoFill();
    await this.router.navigate(['loading']);
    return this.router.navigate(['application-form']);
  }

  async _testAMCemailVerifyCoverpath(): Promise<boolean> {
    // whitelist
    // resend email verification
    await this.authService.whiteListAMCemail();
    await this.applicationService.resendEmailVerification();
    await this.router.navigate(['loading']);
    return this.router.navigate(['application-form']);
  }

  async _testAMCSetEmailVerified(): Promise<boolean> {
    await this.authService.getAMCController().setAMCEmailVerified();
    await this.router.navigate(['loading']);
    return this.router.navigate(['application-form']);
  }

  removeAdvisorEditBanner(): void {
    this.showAdvisorEditBanner = false;
  }

  removeAdvisorAnsweredBanner(): void {
    this.showAdvisorAnsweredBanner = false;
  }

  showLifeBridgeBanner(): boolean {
    return this.isLifeBridge && [1, 3, 4, 6, 7, 8].includes(this.currentSection);
  }

  showSectionValidationToast(): void {
    if (this.toastBySubjectOpen) {
      return;
    }

    const toastOptions = {
      linkLabel: 'Take me there',
    };

    this.applicationToaster.notify('You missed a required question', toastOptions);
  }

  navigateToErrorSection(): void {
    const errorSection = this.appState.getFirstIncompleteSection();
    const cpFilingType = this.appState.controller.customer.getCoverpathFilingType();
    const allSections = ApplicationSectionUtils.getAllSections(cpFilingType, this.appState);
    const primaryPolicy = this.amorphicService.controller.customer.application.primaryPolicy;

    if (errorSection > -1 && errorSection !== this.currentSection && errorSection + 1 <= allSections.length) {
      if (
        this.controller.customer.application.lockedByAdvisor &&
        this.controller.isQuestionLockEnabled &&
        this.appState.isAdvisor
      ) {
        const lockedQuestionErrorSection = this.appState.getFirstIncompleteLockedQuestionSection();
        this.navigationService.activeApplicationSection$.next(lockedQuestionErrorSection);
      } else {
        this.navigationService.activeApplicationSection$.next(errorSection);
      }
      this.afterActiveSectionLoadedSubscription = this.navigationService.afterActiveSectionLoaded$
        .pipe(take(1))
        .subscribe(() => {
          this.scrollToValidationError();
        });
    } else {
      // Check to see if it's GIACT error (it's not part of normal validation)
      if (primaryPolicy.isGiactWebDebitValid === false) {
        const cpFilingType = this.amorphicService.controller.customer.getCoverpathFilingType();
        const paymentSection =
          cpFilingType === CPMetadataFilingTypes.CP2020 ? 'Payment' : ApplicationSectionsList.PAYMENT_METHOD;
        this.navigateToSection(paymentSection);
      } else {
        this.scrollToValidationError();
      }
    }
  }

  navigateToSection(section: string): void {
    const sectionIndex = this.appState.getSectionIndex(section);
    if (sectionIndex > -1 && sectionIndex !== this.currentSection) {
      this.navigationService.activeApplicationSection$.next(sectionIndex);
    }
  }

  getFirstInvalidChildNode(node: CasMetadataNode): CasMetadataNode {
    // If a question has children, recurse down to find the first child that is the source of the error
    let current: CasMetadataNode = node;
    while (current.children && current.children.some((q) => !q.isValid(q.loopIndex) && q.isShown())) {
      const child = current.children.filter((q) => !q.isValid(q.loopIndex) && q.isShown())[0];
      if (this.getScrollPoint(child)) {
        current = child;
      } else {
        break;
      }
    }
    return current;
  }

  scrollToValidationError(): void {
    // Requires DOM to retrieve invalid application questions
    if (!document) {
      return;
    }
    this.applicationToaster.close();
    // Get values for first invalid input in the current section
    const invalidQuestions: CasMetadataNode[] = this.applicationService.getInvalidSectionQuestions(this.currentSection);
    if (!invalidQuestions || !invalidQuestions[0]) {
      CoverpathUtils.scrollToTop(true);
      return;
    }

    // Iterate through the questions to find the one rendered highest up on the page
    let targetQuestion: CasMetadataNode;
    let minHeight: number = Number.MAX_SAFE_INTEGER;
    invalidQuestions.forEach((question: CasMetadataNode) => {
      const scrollPoint = this.getScrollPoint(question);
      if (scrollPoint && scrollPoint.getBoundingClientRect().top < minHeight) {
        minHeight = Math.min(minHeight, scrollPoint.getBoundingClientRect().top);
        targetQuestion = question;
      }
    });
    if (!targetQuestion) {
      CoverpathUtils.scrollToTop(true);
      return;
    }
    targetQuestion = this.getFirstInvalidChildNode(targetQuestion);
    // Attempt to scroll to element, otherwise scroll to the top of the page
    const targetScrollPoint: HTMLElement = this.getScrollPoint(targetQuestion);
    if (targetScrollPoint) {
      // Validate targetQuestion to show error state when scrolled to
      const propsToValidate: string[] = this.applicationService.extractSubPropStrings(targetQuestion);
      SubjectManager.publish('validate_previous', propsToValidate);
      targetScrollPoint.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    } else {
      CoverpathUtils.scrollToTop(true);
    }

    if (targetQuestion.isJuviSignerExceptionNode()) {
      this.modalService.activate({
        type: ModalType.signerEmail,
        notShowClose: true,
        disableEscapeKeyPress: true,
      });
    }
  }

  getScrollPoint(node: CasMetadataNode): HTMLElement {
    let scrollId: string;
    let collectionIndex: number;
    if (node.parent.isIterateCollection()) {
      // For iterate-collects, find which collection item contains the error
      const arrayValues = Utils.get(this.controller, node.parent.prop) || [];
      for (let i = 0; i < arrayValues.length; i++) {
        if (!node.isValid(i)) {
          collectionIndex = i;
          break;
        }
      }
    }
    if (collectionIndex === 0 && node.parent.children[0] === node) {
      // Scroll to the whole iterate-collection block for it's first input field.
      // iterate-collects often contain a non-repeated label at the beginning.
      scrollId = `label-scroll-point__${node.parent.id}`;
    } else if (collectionIndex) {
      scrollId = `label-scroll-point__${node.id}[${collectionIndex}]`;
    } else {
      scrollId = `label-scroll-point__${node.id}`;
    }
    return document.getElementById(scrollId);
  }

  listenSticky() {
    const elem = this.topBanner.nativeElement;
    const content = this.topBannerContent.nativeElement;
    window.addEventListener('scroll', () => {
      const stuck = elem.getBoundingClientRect().top < 0;
      const hasClass = content.classList.contains('toast-stuck');
      if (stuck && !hasClass) {
        content.classList.add('toast-stuck');
      } else if (!stuck && hasClass) {
        content.classList.remove('toast-stuck');
      }
    });
  }

  ngOnDestroy() {
    if (this.missedQuestionSubscription) {
      this.missedQuestionSubscription.unsubscribe();
    }
    if (this.loadingSubscription) {
      this.loadingSubscription.unsubscribe();
    }
    if (this.editTeleCMIModalSubscription) {
      this.editTeleCMIModalSubscription.unsubscribe();
    }
    if (this.applicationSectionChangesSubscription) {
      this.applicationSectionChangesSubscription.unsubscribe();
    }
    if (this.activeApplicationSubscription) {
      this.activeApplicationSubscription.unsubscribe();
    }
    if (this.afterActiveSectionLoadedSubscription) {
      this.afterActiveSectionLoadedSubscription.unsubscribe();
    }
    if (this.teleMissedQuestionSubscription) {
      this.teleMissedQuestionSubscription.unsubscribe();
    }
    if (this.toastIsOpenSubscription) {
      this.toastIsOpenSubscription.unsubscribe();
    }
  }
}
